import { useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import {
  Button, Field, Modal, PencilIcon, SearchIcon, SectionHeader, SelectField, StatusBadge, ToggleField,
  type Column,
} from "@fawkes/ui";

import { VOEDING_SOORTEN, eenheidVan, type VoedingProduct, type VoedingSoort } from "../Voedingsplan";
import type { SettingsContext } from "./tiles/types";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

interface Bewerk { id: number | null; naam: string; soort: VoedingSoort; kcal: string; actief: boolean; vestigingId: number | null }

/**
 * Instellingen › Voeding: de producten waaruit je een voedingsplan samenstelt — brok, natvoer en vocht met hun
 * calorische waarde (Alma, Roze, 2026-10-09). Per locatie (Maarten: "zorg dat ze per locatie ingesteld kunnen
 * worden"): bij een vestiging beheer je de producten van die vestiging en zie je de algemene erbij; onder Organisatie
 * beheer je de producten die voor alle locaties gelden. Een product gaat niet weg maar wordt uitgezet: oude plannen
 * noemen het nog.
 */
export function VoedingSettings({ context, onBack }: { context: SettingsContext; onBack: () => void }) {
  const t = useTranslate();
  const vestigingId = context.type === "branch" ? context.vestigingId : null;
  const url = vestigingId != null ? `voeding-producten?vestigingId=${vestigingId}` : "voeding-producten?algemeen=true";
  const { data, isLoading, isError, refetch } = useCustom<VoedingProduct[]>({ url, method: "get" });
  const { mutate, isLoading: bezig } = useCustomMutation();
  const producten = Array.isArray(data?.data) ? data!.data : [];
  const [soort, setSoort] = useState<VoedingSoort>("BROK");
  const [zoek, setZoek] = useState("");
  const [bewerk, setBewerk] = useState<Bewerk | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  const naald = zoek.trim().toLowerCase();
  const rijen = producten.filter((p) => p.soort === soort && (!naald || p.naam.toLowerCase().includes(naald)));
  // Bij een vestiging zijn de algemene producten te zien maar niet te wijzigen: die horen bij Organisatie.
  const magWijzigen = (p: VoedingProduct) => vestigingId == null || p.vestigingId === vestigingId;

  const bewaar = () => {
    if (!bewerk || !bewerk.naam.trim()) return;
    const kcal = bewerk.kcal.trim() === "" ? null : Number(bewerk.kcal.replace(",", "."));
    mutate(
      {
        url: bewerk.id == null ? "voeding-producten" : `voeding-producten/${bewerk.id}`,
        method: bewerk.id == null ? "post" : "put",
        values: { naam: bewerk.naam.trim(), soort: bewerk.soort, kcalPer100: kcal, actief: bewerk.actief, vestigingId: bewerk.vestigingId },
        successNotification: false, errorNotification: false,
      },
      {
        onSuccess: () => { setBewerk(null); setFout(null); void refetch(); },
        onError: (e) => setFout((e as { message?: string }).message || t("voedingsplan.fout")),
      },
    );
  };

  const kolommen: Column<VoedingProduct>[] = [
    { header: t("voedingsproducten.kolom.product"), cell: (p) => <strong className={p.actief ? undefined : "vs-uit"}>{p.naam}</strong> },
    {
      header: t("voedingsproducten.kolom.kcal"), align: "right",
      cell: (p) => (p.kcalPer100 != null
        ? `${String(p.kcalPer100).replace(".", ",")} kcal / 100 ${eenheidVan(p.soort)}`
        : <span className="vs-muted">{t("voedingsproducten.geenKcal")}</span>),
    },
    {
      header: t("voedingsproducten.kolom.locatie"),
      cell: (p) => (p.vestigingId == null
        ? <StatusBadge tone="neutral">{t("voedingsproducten.alleLocaties")}</StatusBadge>
        : <StatusBadge tone="info">{context.label}</StatusBadge>),
    },
    {
      header: t("voedingsproducten.kolom.status"),
      cell: (p) => <StatusBadge tone={p.actief ? "success" : "neutral"}>{t(p.actief ? "teamsBeheer.actief" : "teamsBeheer.uitgezet")}</StatusBadge>,
    },
    {
      header: "", align: "right",
      cell: (p) => magWijzigen(p) ? (
        <button type="button" className="vs-actie" aria-label={`${t("voedingsproducten.wijzig")}: ${p.naam}`} title={t("voedingsproducten.wijzig")}
          onClick={() => setBewerk({ id: p.id, naam: p.naam, soort: p.soort, kcal: p.kcalPer100 == null ? "" : String(p.kcalPer100), actief: p.actief, vestigingId: p.vestigingId ?? null })}>
          <PencilIcon width={16} height={16} />
        </button>
      ) : null,
    },
  ];

  return (
    <div className="settings-finance-settings vs">
      <div className="settings-finance-detail-toolbar">
        <Button variant="secondary" onClick={onBack}>{t("common.back")}</Button>
        <SectionHeader>{t("instellingen.tile.voeding.title")}</SectionHeader>
      </div>
      <p className="fawkes-muted settings-finance-detail__breadcrumb">
        {`${t("instellingen.title")} › ${context.label} › ${t("instellingen.tile.voeding.title")}`}
      </p>

      <section className="vs-kaart">
        <div className="vs-kop">
          <div>
            <h3>{t("voedingsproducten.titel")}</h3>
            <p>{t(vestigingId != null ? "voedingsproducten.uitlegVestiging" : "voedingsproducten.uitlegOrganisatie")}</p>
          </div>
          <Button variant="primary" onClick={() => setBewerk({ id: null, naam: "", soort, kcal: "", actief: true, vestigingId })}>
            {t("voedingsproducten.nieuw")}
          </Button>
        </div>

        <div className="vs-balk">
          <div className="vp-keer" role="tablist" aria-label={t("voedingsplan.soort")}>
            {VOEDING_SOORTEN.map((s) => (
              <button key={s} type="button" role="tab" aria-selected={soort === s}
                className={`vp-keer__knop${soort === s ? " is-actief" : ""}`} onClick={() => setSoort(s)}>
                {t(`voedingsplan.soort.${s}`)} <span className="vs-aantal">{producten.filter((p) => p.soort === s).length}</span>
              </button>
            ))}
          </div>
          <div className="vs-zoek">
            <SearchIcon width={14} height={14} aria-hidden="true" />
            <input className="fui-field__input" placeholder={t("voedingsproducten.zoek")} aria-label={t("voedingsproducten.zoek")}
              value={zoek} onChange={(e) => setZoek(e.target.value)} />
          </div>
        </div>

        {isError ? (
          <p className="vs-muted">{t("instellingen.dieren.loadFailed")}</p>
        ) : (
          <IndeelbareTabel tabelKey="settings-voeding-settings" columns={kolommen} rows={rijen} getRowKey={(p) => String(p.id)}
            empty={isLoading ? t("common.loading") : t("voedingsproducten.leeg")} />
        )}
      </section>

      <Modal
        open={bewerk != null}
        title={t(bewerk?.id == null ? "voedingsproducten.nieuw" : "voedingsproducten.wijzig")}
        onClose={() => setBewerk(null)}
        footer={bewerk && (
          <>
            <Button variant="secondary" onClick={() => setBewerk(null)} disabled={bezig}>{t("common.cancel")}</Button>
            <Button variant="primary" onClick={bewaar} disabled={bezig || !bewerk.naam.trim()}>{t("common.save")}</Button>
          </>
        )}
      >
        {bewerk && (
          <div className="vs-form">
            <Field label={t("voedingsplan.productNaam")} value={bewerk.naam} required maxLength={150}
              onChange={(e) => setBewerk({ ...bewerk, naam: e.target.value })} />
            <SelectField label={t("voedingsplan.soort")} value={bewerk.soort}
              options={VOEDING_SOORTEN.map((s) => ({ value: s, label: t(`voedingsplan.soort.${s}`) }))}
              onChange={(e) => setBewerk({ ...bewerk, soort: e.target.value as VoedingSoort })} />
            <Field label={t("voedingsplan.kcalPer100")} inputMode="decimal" value={bewerk.kcal}
              onChange={(e) => setBewerk({ ...bewerk, kcal: e.target.value })} />
            <p className="vs-hint">{t("voedingsproducten.kcalHint")}</p>
            <p className="vs-hint">
              {bewerk.vestigingId == null ? t("voedingsproducten.voorAlle") : t("voedingsproducten.voorVestiging").replace("%s", context.label)}
            </p>
            {bewerk.id != null && (
              <ToggleField label={t(bewerk.actief ? "teamsBeheer.actief" : "teamsBeheer.uitgezet")} checked={bewerk.actief}
                onChange={(e) => setBewerk({ ...bewerk, actief: e.target.checked })} />
            )}
            {fout && <p className="fawkes-error" role="alert">{fout}</p>}
          </div>
        )}
      </Modal>
    </div>
  );
}
